import {
  getRequiredValidator,
  minMaxValidator,
  positiveOrNullValidator,
  uniqueNarrativePerEquityInstrumentType2,
} from '@app/core/validations/hook-forms/validators';
import FormField from '@app/shared/components/form-controls/form-field/FormField';
import { swapMaskForValueDisplay } from '@app/shared/components/grid-controls/grid-text-field/grid-cell-value-patterns';
import { FC, useEffect, useMemo } from 'react';
import { EquityItem } from './CapitalStructureItemFormERF2';
import { useLocale } from '@app/core/hooks/useLocale';
import {
  getAllInstrumentsFromBuildStructure,
  getEventInstrumentsFromBuildStructure,
  selectBuildStructuresResults,
} from '@app/core/store/capital-structure-slice-selectors';
import { useAppSelector } from '@app/core/hooks/redux-hooks';
import useIsErfProject from '@app/shared/hooks/useIsErfProject';
import { Toggle } from '@app/shared/components/toggle/Toggle';
import {
  percentageFieldFormattingProps,
  percentageFieldWithoutPlaceholderFormattingProps,
} from '@app/shared/components/form-controls/form-field/form-field-patterns';
import FormDatePicker from '@app/shared/components/form-controls/form-date-picker/FormDatePicker';
import { FormSelect } from '@app/shared/components/form-controls/form-select/FormSelect';
import { enumKeyByValue, enumToOptions } from '@app/shared/helpers';
import {
  CouponType,
  EventChangedType,
  InstrumentType,
  Dates,
} from '@app/shared/models/contracts/enums/shared-enums';
import styles from './capital-structure-item-form-2.module.scss';
import Button from '@app/shared/components/button/Button';
import SvgArrowUp from '@app/shared/icons/ArrowUp';
import { ButtonAppearance, ButtonSize } from '@app/shared/components/button/button-enums';
import SvgArrowDown from '@app/shared/icons/ArrowDown';
import { useFormContext } from 'react-hook-form';
import { Tooltip, TooltipPlacement } from '@app/shared/components/tooltip/Tooltip';
import SvgInformation from '@app/shared/icons/Information';
import { selectIsCompatibilityMode } from '@app/core/store/project-slice-selectors';

interface Props {
  equityItem: EquityItem;
  eventSetId: string;
  isEvent: boolean;
  isBeforeOrOnValuationDate: boolean;
  submitForm: (rankValue?: number) => void;
  hasCouponDateError?: boolean;
}

const PARENT_CLASSNAME = 'capital-structure-form';
const MIN_DATE = Dates.MinDate;

export const CapitalStructureItemFormFeatures: FC<Props> = ({
  equityItem,
  eventSetId,
  isEvent,
  isBeforeOrOnValuationDate,
  submitForm,
  hasCouponDateError,
}) => {
  const { l } = useLocale();
  const isErfProject = useIsErfProject();
  const buildStructuresResults = useAppSelector(selectBuildStructuresResults);
  const activeEventId = useAppSelector((state) => state.uiValues.activeInstrument?.eventId ?? '');
  const isCompatibilityMode = useAppSelector(selectIsCompatibilityMode);
  const ordinaryEquityKey = enumKeyByValue(InstrumentType, InstrumentType.OrdinaryEquity);
  const isOrdinaryEquity = equityItem.type === ordinaryEquityKey;
  const isInstrumentAddedOnThisEvent =
    (equityItem.changeFlags & EventChangedType.InstrumentAdded) ===
    EventChangedType.InstrumentAdded;

  const allInstruments = useMemo(
    () => getAllInstrumentsFromBuildStructure(buildStructuresResults),
    [buildStructuresResults]
  );
  const eventInstruments = useMemo(
    () => getEventInstrumentsFromBuildStructure(buildStructuresResults, eventSetId, activeEventId),
    [buildStructuresResults, eventSetId, activeEventId]
  );

  const instrumentNarrativeFieldName = 'instrumentNarrative';
  const COUPON_TYPE_OPTIONS = enumToOptions(CouponType);

  const { watch, setValue } = useFormContext();
  const isDividendEligible = watch('dividendEligible');

  const couponTypeOptions = useMemo(() => {
    // used to change "No Coupon" display value to empty string
    const result = COUPON_TYPE_OPTIONS.map((option) => {
      if (option.value === enumKeyByValue(CouponType, CouponType.NoCoupon)) {
        return { value: undefined, viewValue: '' };
      }
      return { ...option };
    });

    return result;
  }, [COUPON_TYPE_OPTIONS]);

  const isCouponFieldsDisabled =
    Boolean(hasCouponDateError) || equityItem.previousActiveCoupon?.date === MIN_DATE;

  const filterRelevantInstruments = () => {
    const filterCondition =
      equityItem.type === ordinaryEquityKey
        ? (i: EquityItem) => i.type === ordinaryEquityKey
        : (i: EquityItem) => i.type !== ordinaryEquityKey;

    return eventInstruments
      .filter((i) => i.instrumentId !== equityItem.instrumentId)
      .filter((i) => filterCondition(i));
  };

  const canIncreaseRank = () => {
    return (
      eventInstruments.length > 0 &&
      filterRelevantInstruments().some((i) => i.rank >= equityItem.rank)
    );
  };

  const canDecreaseRank = () => {
    return (
      eventInstruments.length > 0 &&
      equityItem.rank > 0 &&
      filterRelevantInstruments().some((i) => i.rank <= equityItem.rank)
    );
  };

  const getNextRank = () => {
    const instrumentGroup = filterRelevantInstruments()
      .map((i) => i.rank)
      .filter((r) => r > equityItem.rank);

    if (instrumentGroup.length < 1) {
      return equityItem.rank + 1;
    }

    return Math.min(...instrumentGroup);
  };

  const getPreviousRank = () => {
    const instrumentGroup = filterRelevantInstruments()
      .map((i) => i.rank)
      .filter((r) => r < equityItem.rank);

    if (instrumentGroup.length < 1) {
      return equityItem.rank - 1;
    }

    return Math.max(...instrumentGroup, 0);
  };

  const disableIncreaseButton = !canIncreaseRank();
  const disableDecreaseButton = !canDecreaseRank();
  const nextRankValue = getNextRank();
  const previousRankValue = getPreviousRank();

  const getCouponTypeDisplayValue = (value: string | null | number | undefined) => {
    const option = COUPON_TYPE_OPTIONS.find((opt) => opt.value === value);
    return option ? option.viewValue : value;
  };

  const PreviouslyLabel: FC<{
    value: string | null | number | undefined;
    type?: 'percentage' | 'enum' | 'string';
  }> = ({ value, type = 'string' }) =>
    value !== undefined && value !== null && value !== '' ? (
      <div className={styles['previously-label']}>
        <span>{l('_PreviouslyLabel')}</span>
        <span className={styles['previous-value']}>
          {type === 'percentage'
            ? `${(Number(value) * 100).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}%`
            : type === 'enum'
            ? getCouponTypeDisplayValue(value)
            : value}
        </span>
      </div>
    ) : null;

  useEffect(() => {
    if (isCouponFieldsDisabled) {
      // Clear coupon fields if there's a coupon date error (e.g., event date before coupon start date)
      setValue('coupon.value', undefined, { shouldDirty: false, shouldValidate: false });
      setValue('coupon.type', undefined, { shouldDirty: false, shouldValidate: false });
      setValue('coupon.cashPayProportion', undefined, {
        shouldDirty: false,
        shouldValidate: false,
      });
      setValue('coupon.date', undefined, { shouldDirty: false, shouldValidate: false });
    }
  }, [isCouponFieldsDisabled, setValue]);

  return (
    <>
      <div className={styles[`${PARENT_CLASSNAME}__features`]}>
        <h2 className="heading-5">
          {isErfProject ? l('_EditInstrument') : l('_InstrumentDetails')}
        </h2>
        {isOrdinaryEquity && (
          <FormField
            name={instrumentNarrativeFieldName}
            label={l('_InstrumentNarrative')}
            required
            rules={{
              ...getRequiredValidator(),
              ...uniqueNarrativePerEquityInstrumentType2(allInstruments, equityItem),
            }}
            isInputMasked={false}
            inlineLabel={isErfProject}
            fieldValueMask={swapMaskForValueDisplay}
            disabled={!isInstrumentAddedOnThisEvent}
          />
        )}
        {!isOrdinaryEquity && !isInstrumentAddedOnThisEvent && (
          <>
            <FormField
              name={instrumentNarrativeFieldName}
              label={l('_InstrumentNarrative')}
              isInputMasked={false}
              inlineLabel={isErfProject}
              fieldValueMask={swapMaskForValueDisplay}
              disabled
            />
            <div className={styles['form-field-spacer']}>
              <FormField
                name="coupon.value"
                label={
                  <span className={styles['control__label-tooltip']}>
                    {l('_CouponPc')}
                    {isCouponFieldsDisabled && (
                      <Tooltip
                        content={l('_CannotEditCouponBeforeStartDate')}
                        placement={TooltipPlacement.Right}>
                        <Button
                          appearance={ButtonAppearance.CLEAN}
                          size={ButtonSize.FLUID}
                          aria-label={l('_MoreInformation')}>
                          <SvgInformation className={styles['toggle--warning-icon']} />
                        </Button>
                      </Tooltip>
                    )}
                  </span>
                }
                data-testid="instrument-coupon-amount"
                required={false}
                inlineLabel={isErfProject}
                isGapless
                disabled={isCouponFieldsDisabled}
                {...percentageFieldWithoutPlaceholderFormattingProps}
              />
              {!isCouponFieldsDisabled && (
                <div className={styles['previously-label-container']}>
                  <PreviouslyLabel
                    value={equityItem.previousActiveCoupon?.value}
                    type="percentage"
                  />
                </div>
              )}
            </div>
            <>
              <div className={styles['form-field-spacer']}>
                <FormSelect
                  ariaLabel={l('_SelectorAriaLabel', { label: l('_CouponType') })}
                  name="coupon.type"
                  disabled={isCouponFieldsDisabled}
                  required={false}
                  inlineLabel={isErfProject}
                  label={
                    <span className={styles['control__label-tooltip']}>
                      {l('_CouponType')}
                      {isCouponFieldsDisabled && (
                        <Tooltip
                          content={l('_CannotEditCouponBeforeStartDate')}
                          placement={TooltipPlacement.Right}>
                          <Button
                            appearance={ButtonAppearance.CLEAN}
                            size={ButtonSize.FLUID}
                            aria-label={l('_MoreInformation')}>
                            <SvgInformation className={styles['toggle--warning-icon']} />
                          </Button>
                        </Tooltip>
                      )}
                    </span>
                  }
                  onChange={() => submitForm()}
                  options={couponTypeOptions}
                  className={styles['control__select']}
                  isGapless
                />
                {!isCouponFieldsDisabled && (
                  <div className={styles['previously-label-container']}>
                    <PreviouslyLabel value={equityItem.previousActiveCoupon?.type} type="enum" />
                  </div>
                )}
              </div>
              {!isCompatibilityMode && (
                <div className={styles['form-field-spacer']}>
                  <FormField
                    name="coupon.cashPayProportion"
                    label={
                      <span className={styles['control__label-tooltip']}>
                        {l('_CashPayProportion')}
                        {isCouponFieldsDisabled && (
                          <Tooltip
                            content={l('_CannotEditCouponBeforeStartDate')}
                            placement={TooltipPlacement.Right}>
                            <Button
                              appearance={ButtonAppearance.CLEAN}
                              size={ButtonSize.FLUID}
                              aria-label={l('_MoreInformation')}>
                              <SvgInformation className={styles['toggle--warning-icon']} />
                            </Button>
                          </Tooltip>
                        )}
                      </span>
                    }
                    disabled={isCouponFieldsDisabled}
                    data-testid="instrument-cash-pay-proportion"
                    required={false}
                    inlineLabel={isErfProject}
                    {...{
                      ...percentageFieldWithoutPlaceholderFormattingProps,
                    }}
                    rules={{
                      ...minMaxValidator(0, 100),
                    }}
                    isGapless
                  />
                  {!isCouponFieldsDisabled && (
                    <div className={styles['previously-label-container']}>
                      <PreviouslyLabel
                        value={equityItem.previousActiveCoupon?.cashPayProportion}
                        type="percentage"
                      />
                    </div>
                  )}
                </div>
              )}
            </>
          </>
        )}
        {!isOrdinaryEquity && isInstrumentAddedOnThisEvent && (
          <>
            <FormField
              name={instrumentNarrativeFieldName}
              label={l('_InstrumentNarrative')}
              required
              rules={{
                ...getRequiredValidator(),
                ...uniqueNarrativePerEquityInstrumentType2(allInstruments, equityItem),
              }}
              isInputMasked={false}
              inlineLabel={isErfProject}
              fieldValueMask={swapMaskForValueDisplay}
            />
            <FormDatePicker
              name="coupon.date"
              label={l('_CouponStartDate')}
              disabled={equityItem.coupon?.value === null}
              required
              data-testid="instrument-coupon-start-date"
              inlineLabel={isErfProject}
              readOnly
              rules={{}}
            />
            <>
              <FormField
                name="coupon.value"
                label={l('_CouponPc')}
                data-testid="instrument-coupon-amount"
                inlineLabel={isErfProject}
                {...{
                  ...percentageFieldFormattingProps,
                  placeholder:
                    isEvent && equityItem.coupon && equityItem.coupon.value
                      ? new Intl.NumberFormat('default', {
                          style: 'percent',
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(equityItem.coupon.value)
                      : percentageFieldFormattingProps.placeholder,
                }}
                rules={{
                  validate: {
                    isPositive: positiveOrNullValidator(true).validate,
                  },
                  ...(!isEvent ? getRequiredValidator() : {}),
                }}
              />
              <FormSelect
                ariaLabel={l('_SelectorAriaLabel', { label: l('_CouponType') })}
                name="coupon.type"
                disabled={equityItem.coupon?.value === null}
                required
                inlineLabel={isErfProject}
                label={l('_CouponType')}
                defaultValue={CouponType.CompoundInterest}
                onChange={() => submitForm()}
                options={COUPON_TYPE_OPTIONS.filter(
                  (option) => option.value !== enumKeyByValue(CouponType, CouponType.NoCoupon)
                )}
                className={styles['control__select']}
              />
              {!isCompatibilityMode && (
                <FormField
                  name="coupon.cashPayProportion"
                  label={l('_CashPayProportion')}
                  disabled={equityItem.coupon?.value === null}
                  data-testid="instrument-cash-pay-proportion"
                  required
                  inlineLabel={isErfProject}
                  {...percentageFieldFormattingProps}
                  rules={{
                    ...minMaxValidator(0, 100),
                  }}
                />
              )}
            </>
          </>
        )}
        {ordinaryEquityKey !== equityItem.type && (!isEvent || isInstrumentAddedOnThisEvent) && (
          <div className={styles['control']}>
            <span
              className={isErfProject ? styles['control__label-erf'] : styles['control__label']}>
              {l('_Rank')}
            </span>
            <div className={styles['control__item']}>
              <div className={styles['rank']}>
                <Button
                  appearance={ButtonAppearance.ROUND}
                  onClick={() => submitForm(nextRankValue)}
                  disabled={disableIncreaseButton}
                  endIcon={<SvgArrowUp className={styles['rank__button-icon']} />}
                  data-testid="rank-up"
                />
                <Button
                  appearance={ButtonAppearance.ROUND}
                  onClick={() => submitForm(previousRankValue)}
                  disabled={disableDecreaseButton}
                  endIcon={<SvgArrowDown className={styles['rank__button-icon']} />}
                  data-testid="rank-down"
                />
              </div>
            </div>
          </div>
        )}
        {ordinaryEquityKey === equityItem.type && (!isEvent || isInstrumentAddedOnThisEvent) && (
          <div className={styles['control']}>
            <span
              className={isErfProject ? styles['control__label-erf'] : styles['control__label']}>
              {l('_SweetEquity')}
            </span>
            <div className={styles['control__item']}>
              <Toggle
                name="isSweetEquity"
                label={l('_SweetEquity')}
                isLabelHidden
                onChange={() => submitForm()}
              />
            </div>
          </div>
        )}
        {isBeforeOrOnValuationDate && isInstrumentAddedOnThisEvent && (
          <div className={styles['control']}>
            <span
              className={isErfProject ? styles['control__label-erf'] : styles['control__label']}>
              {l('_ValueInstrument')}
            </span>
            <div className={styles['control__item']}>
              <Toggle
                name="shouldBeValued"
                label={l('_ValueInstrument')}
                isLabelHidden
                onChange={() => submitForm()}
              />
            </div>
          </div>
        )}
        {isOrdinaryEquity && !isCompatibilityMode && isInstrumentAddedOnThisEvent && (
          <div className={styles['control']}>
            <div className={styles['control__label-tooltip']}>
              <span
                className={isErfProject ? styles['control__label-erf'] : styles['control__label']}>
                {l('_DividendEligible')}
              </span>
              {isDividendEligible && (
                <Tooltip content={l('_EnterDividendData')} placement={TooltipPlacement.Top}>
                  <Button
                    appearance={ButtonAppearance.CLEAN}
                    size={ButtonSize.FLUID}
                    aria-label={l('_MoreInformation')}>
                    <SvgInformation className={styles['toggle--warning-icon']} />
                  </Button>
                </Tooltip>
              )}
            </div>
            <div className={styles['control__item']}>
              <Toggle
                name="dividendEligible"
                label={l('_DividendEligible')}
                isLabelHidden
                onChange={() => submitForm()}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};
